// Sketch Display Component
// Organized for Supabase Migration

import React, { useRef } from 'react';
import { Camera } from 'lucide-react';
import { 
  TriangleComponent, 
  CircleComponent, 
  SquarePyramidComponent,
  ParabolaComponent,
  FunctionComponent,
  CircleWithChordsComponent,
  CircularConeComponent,
  CuboidDiagonalsComponent,
  IntersectingPlanesTriangleComponent,
  SphereCrossSectionComponent,
  PointToPlaneGeometryComponent,
  PentagonInCircleComponent,
  CyclicQuadrilateralComponent
} from './SVGComponents.jsx';
import { generateHighlightedCode } from '../utils/sketchUtils.js';

export const SketchDisplay = ({
  currentSketch,
  showCoordinates,
  showGrid,
  getCurrentParams,
  handlePointDrag,
  showJSON,
  showCode,
  showParameters,
  getSketchPoints,
  copyJSONToClipboard,
  copyCodeToClipboard,
  viewportWidth,
  viewportHeight,
  viewportScale,
  setViewportHeight
}) => {
  const svgRef = useRef(null);

  const renderSketchComponent = () => {
    if (!currentSketch) return null;

    const params = getCurrentParams();
    const commonProps = {
      params,
      showCoords: showCoordinates,
      onPointDrag: handlePointDrag,
      svgRef,
      viewportWidth,
      viewportHeight,
      viewportScale
    };

    // Map sketch names to components
    const sketchComponents = {
      'Triangle': TriangleComponent,
      'Circle': CircleComponent,
      'Square Pyramid': SquarePyramidComponent,
      'Parabola': ParabolaComponent,
      'Function Roots & Horizontal Line': FunctionComponent,
      'Circle with Intersecting Chords': CircleWithChordsComponent,
      'Pentagon in Circle': PentagonInCircleComponent,
      'Cyclic Quadrilateral': CyclicQuadrilateralComponent,
      'Triangle with Angles': TriangleComponent,
      'Triangle with Interior Angles': TriangleComponent,
      'Midpoint Theorem': TriangleComponent,
      'Bearing & Direction Triangle': TriangleComponent,
      'Double Triangle Construction': TriangleComponent,
      'Intersecting Planes': SquarePyramidComponent, // Reuse 3D component
      'Pyramid Multiple Views': SquarePyramidComponent,
      'Detected Triangle': TriangleComponent,
      'Detected Circle': CircleComponent,
      'Detected Pyramid': SquarePyramidComponent,
      'Detected Parabola': ParabolaComponent,
      'Detected Function': FunctionComponent,
      // NEW ADVANCED GEOMETRIC SKETCHES
      'Circular Cone': CircularConeComponent,
      'Cuboid with Diagonals': CuboidDiagonalsComponent,
      'Intersecting Planes with Triangle': IntersectingPlanesTriangleComponent,
      'Sphere Cross-Section': SphereCrossSectionComponent,
      'Point to Plane Geometry': PointToPlaneGeometryComponent
    };

    const SketchComponent = sketchComponents[currentSketch.name] || TriangleComponent;
    return <SketchComponent {...commonProps} />;
  };

  const renderJSONPanel = () => {
    if (!showJSON || !currentSketch) return null;

    const currentParams = getCurrentParams();
    const sketchPoints = getSketchPoints(currentSketch);
    const currentSketchJSON = {
      name: currentSketch.name,
      category: currentSketch.category_id,
      description: currentSketch.description,
      coordinates: currentSketch.coordinates,
      type: currentSketch.type,
      points: sketchPoints,
      properties: {
        shape: currentSketch.name.toLowerCase(),
        ...currentParams
      },
      parameters: currentParams,
      generatedFrom: currentSketch.isGenerated ? "Image Analysis" : currentSketch.isImported ? "JSON Import" : "Manual Creation",
      generatedDate: new Date().toISOString(),
      version: "3.0"
    };

    return (
      <div className="mt-4 bg-gray-900 rounded-lg p-4">
        <div className="flex items-center justify-between mb-3">
          <h4 className="font-semibold text-white">
            Current Sketch JSON 
            {currentSketch.isGenerated && <span className="text-green-400">(AI Generated)</span>}
            {currentSketch.isImported && <span className="text-blue-400">(Imported)</span>}
          </h4>
          <button
            onClick={() => copyJSONToClipboard(currentSketch)}
            className="text-xs bg-blue-600 text-white px-3 py-1 rounded hover:bg-blue-700 transition-colors"
          >
            Copy to Clipboard
          </button>
        </div>
        <pre className="text-green-400 text-xs overflow-x-auto max-h-60 scrollbar-thin scrollbar-thumb-gray-600 scrollbar-track-gray-800">
          {JSON.stringify(currentSketchJSON, null, 2)}
        </pre>
      </div>
    );
  };

  const renderCodePanel = () => {
    if (!showCode || !currentSketch) return null;

    const currentParams = getCurrentParams();
    const currentSketchCode = generateHighlightedCode(currentSketch, currentParams);

    return (
      <div className="mt-4 bg-gray-900 rounded-lg p-4">
        <div className="flex items-center justify-between mb-3">
          <h4 className="font-semibold text-white">
            React Code (JSON Parameters Highlighted) 
            {currentSketch.isGenerated && <span className="text-green-400">(AI Generated)</span>}
            {currentSketch.isImported && <span className="text-blue-400">(Imported)</span>}
          </h4>
          <button
            onClick={() => copyCodeToClipboard(currentSketchCode)}
            className="text-xs bg-purple-600 text-white px-3 py-1 rounded hover:bg-purple-700 transition-colors"
          >
            Copy Code
          </button>
        </div>
        <div className="bg-gray-800 rounded p-3 max-h-96 overflow-auto scrollbar-thin scrollbar-thumb-gray-600 scrollbar-track-gray-800">
          <pre className="text-sm">
            <code>
              {currentSketchCode.split('\n').map((line, i) => (
                <div key={i} className={
                  line.includes('✨ HIGHLIGHTED') || line.includes('const [params') || line.includes('"pointA_x"') || line.includes('"baseSize"') || line.includes('params.')
                    ? 'text-yellow-300 bg-yellow-900 bg-opacity-20 px-1'
                    : 'text-blue-300'
                }>
                  {line}
                </div>
              ))}
            </code>
          </pre>
        </div>
      </div>
    );
  };

  const renderCoordinateDetails = () => {
    if (!currentSketch) return null;

    const sketchPoints = getSketchPoints(currentSketch);
    if (!sketchPoints.length) return null;

    return (
      <div className="mt-4 bg-gray-50 rounded-lg p-4">
        <h4 className="font-semibold mb-2">Coordinate Details (Live Updates):</h4>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-sm">
          {sketchPoints.map((point, i) => (
            <div key={i} className="bg-white p-2 rounded border">
              <span className="font-medium text-red-600">{point.name}: </span>
              <span className="font-mono">
                ({point.x}, {point.y}{point.z !== null ? `, ${point.z}` : ''})
              </span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  if (!currentSketch) {
    return (
      <div className={showParameters ? "lg:col-span-2" : "lg:col-span-3"}>
        <div className="bg-gray-100 border-2 border-dashed border-gray-300 rounded-lg p-12 text-center">
          <Camera className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-600 mb-2">No Sketch Selected</h3>
          <p className="text-gray-500 mb-4">Upload an image to generate a sketch, import JSON, or select from existing categories</p>
        </div>
      </div>
    );
  }

  return (
    <div className={showParameters ? "lg:col-span-2" : "lg:col-span-3"}>
      <div className="bg-white border border-gray-300 rounded-lg p-6">
        <div className="mb-4">
          <h2 className="text-2xl font-bold text-gray-800 mb-2">
            {currentSketch.name}
            {currentSketch.isGenerated && 
              <span className="ml-3 text-sm bg-green-100 text-green-700 px-3 py-1 rounded-full">AI Generated</span>
            }
            {currentSketch.isImported && 
              <span className="ml-3 text-sm bg-blue-100 text-blue-700 px-3 py-1 rounded-full">Imported</span>
            }
          </h2>
          <p className="text-gray-600 mb-2">{currentSketch.description}</p>
          <p className="text-sm text-blue-600 font-mono bg-blue-50 p-2 rounded">{currentSketch.coordinates}</p>
        </div>
        
        <div className="bg-gray-50 rounded-lg p-4">
          <div className="flex justify-between items-center mb-2">
            <span className="text-sm text-gray-600">Display Height: {Math.round(viewportHeight * viewportScale)}px</span>
            <div className="flex gap-2">
              <button
                onClick={() => {
                  const newHeight = Math.max(200, Math.min(800, viewportHeight * viewportScale - 50));
                  setViewportHeight(newHeight / viewportScale);
                }}
                className="text-xs bg-gray-200 hover:bg-gray-300 px-2 py-1 rounded"
              >
                -
              </button>
              <button
                onClick={() => {
                  const newHeight = Math.max(200, Math.min(800, viewportHeight * viewportScale + 50));
                  setViewportHeight(newHeight / viewportScale);
                }}
                className="text-xs bg-gray-200 hover:bg-gray-300 px-2 py-1 rounded"
              >
                +
              </button>
            </div>
          </div>
          {renderSketchComponent()}
        </div>

        {renderJSONPanel()}
        {renderCodePanel()}
        {renderCoordinateDetails()}
      </div>
    </div>
  );
};
